import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, CATEGORY_META } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { SlidersHorizontal, X } from "lucide-react";

const SORTS = [
  { v: "featured", l: "Featured" },
  { v: "price-asc", l: "Price: Low to High" },
  { v: "price-desc", l: "Price: High to Low" },
  { v: "rating", l: "Top Rated" },
];

function CheckGroup({ title, options, selected, onToggle, testPrefix }) {
  if (!options || options.length === 0) return null;
  return (
    <div className="border-b border-[#1c1c1c] py-5">
      <div className="text-xs uppercase tracking-[0.2em] text-[#00f0ff] mb-3">{title}</div>
      <div className="space-y-2">
        {options.map((o) => (
          <label key={o} className="flex items-center gap-3 cursor-pointer group" data-testid={`${testPrefix}-${String(o).replace(/\s+/g, "-")}`}>
            <span
              className={`h-4 w-4 border flex items-center justify-center transition-colors ${
                selected.includes(String(o)) ? "bttf-gradient border-transparent" : "border-[#3f3f46] group-hover:border-[#00f0ff]"
              }`}
            >
              {selected.includes(String(o)) && <span className="h-1.5 w-1.5 bg-black" />}
            </span>
            <input type="checkbox" className="hidden" checked={selected.includes(String(o))} onChange={() => onToggle(String(o))} />
            <span className="text-sm text-zinc-300 group-hover:text-white transition-colors">{o}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function Category() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const [facets, setFacets] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [inStock, setInStock] = useState(false);
  const [maxPrice, setMaxPrice] = useState(null);
  const [priceCap, setPriceCap] = useState(null);
  const [sel, setSel] = useState({ brand: [], sub_category: [], size: [], bolt_pattern: [], finish: [], tireSize: [] });
  const [showFilters, setShowFilters] = useState(false);

  const meta = CATEGORY_META[category] || { label: category, tagline: "" };

  useEffect(() => {
    const initialSub = searchParams.get("sub");
    setSel({ brand: [], sub_category: initialSub ? [initialSub] : [], size: [], bolt_pattern: [], finish: [], tireSize: [] });
    api.get("/facets", { params: { category } }).then((r) => {
      setFacets(r.data);
      setMaxPrice(r.data.max_price);
      setPriceCap(r.data.max_price);
    });
  }, [category, searchParams]);

  useEffect(() => {
    setLoading(true);
    const params = { category, sort };
    if (sel.brand.length) params.brand = sel.brand.join(",");
    if (sel.sub_category.length) params.sub_category = sel.sub_category.join(",");
    if (sel.bolt_pattern.length) params.bolt_pattern = sel.bolt_pattern.join(",");
    if (sel.finish.length) params.finish = sel.finish.join(",");
    if (sel.size.length === 1) params.size = sel.size[0];
    if (sel.tireSize.length) params.tire_size = sel.tireSize.join(",");
    if (inStock) params.in_stock = true;
    if (maxPrice != null && priceCap != null && maxPrice < priceCap) params.max_price = maxPrice;
    api.get("/products", { params }).then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, [category, sort, sel, inStock, maxPrice, priceCap]);

  const toggle = (group, val) =>
    setSel((prev) => ({
      ...prev,
      [group]: prev[group].includes(val) ? prev[group].filter((x) => x !== val) : [...prev[group], val],
    }));

  const activeCount = Object.values(sel).flat().length + (inStock ? 1 : 0);

  const clearAll = () => {
    setSel({ brand: [], sub_category: [], size: [], bolt_pattern: [], finish: [], tireSize: [] });
    setInStock(false);
    setMaxPrice(priceCap);
  };

  const isRims = category === "rims";

  const Sidebar = (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="font-display text-2xl">Filters</span>
        {activeCount > 0 && (
          <button onClick={clearAll} data-testid="clear-filters" className="text-xs uppercase tracking-widest text-[#00f0ff] hover:underline">
            Clear ({activeCount})
          </button>
        )}
      </div>
      <label className="flex items-center gap-3 cursor-pointer group py-4 border-b border-[#1c1c1c]" data-testid="filter-instock">
        <span className={`h-4 w-4 border flex items-center justify-center ${inStock ? "bttf-gradient border-transparent" : "border-[#3f3f46]"}`}>
          {inStock && <span className="h-1.5 w-1.5 bg-black" />}
        </span>
        <input type="checkbox" className="hidden" checked={inStock} onChange={() => setInStock((s) => !s)} />
        <span className="text-sm text-zinc-300 group-hover:text-white">In Stock Only</span>
      </label>
      <CheckGroup title="Brand" options={facets?.brands} selected={sel.brand} onToggle={(v) => toggle("brand", v)} testPrefix="filter-brand" />
      <CheckGroup title={isRims ? "Finish" : "Category"} options={isRims ? facets?.finishes : facets?.sub_categories} selected={isRims ? sel.finish : sel.sub_category} onToggle={(v) => toggle(isRims ? "finish" : "sub_category", v)} testPrefix="filter-sub" />
      {isRims && <CheckGroup title="Bolt Pattern" options={facets?.bolt_patterns} selected={sel.bolt_pattern} onToggle={(v) => toggle("bolt_pattern", v)} testPrefix="filter-bolt" />}
      {facets?.tire_sizes?.length > 0 && (
        <CheckGroup title="Tire Size" options={facets.tire_sizes} selected={sel.tireSize} onToggle={(v) => toggle("tireSize", v)} testPrefix="filter-tiresize" />
      )}
      {facets?.sizes?.length > 0 && (
        <CheckGroup title={isRims ? "Diameter (in)" : "Wheel Size (in)"} options={facets.sizes} selected={sel.size} onToggle={(v) => setSel((p) => ({ ...p, size: p.size.includes(v) ? [] : [v] }))} testPrefix="filter-size" />
      )}
      {facets && priceCap != null && (
        <div className="py-5">
          <div className="text-xs uppercase tracking-[0.2em] text-[#00f0ff] mb-3">Max Price: ${Math.round(maxPrice)}</div>
          <input
            type="range"
            data-testid="filter-price"
            min={facets.min_price}
            max={priceCap}
            value={maxPrice ?? priceCap}
            onChange={(e) => setMaxPrice(Number(e.target.value))}
            className="w-full accent-[#00f0ff]"
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="pt-16">
      {/* Hero band */}
      <div className="relative h-56 md:h-72 overflow-hidden border-b border-[#27272a]">
        <img src={meta.image} alt={meta.label} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-8">
          <span className="text-xs uppercase tracking-[0.3em] text-[#00f0ff] mb-2">Shop {meta.label}</span>
          <h1 className="font-display text-6xl md:text-8xl uppercase leading-none">{meta.label}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="flex gap-10">
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24">{Sidebar}</div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <button onClick={() => setShowFilters(true)} data-testid="mobile-filter-btn" className="lg:hidden flex items-center gap-2 border border-[#27272a] px-4 py-2 text-sm">
                  <SlidersHorizontal size={16} /> Filters {activeCount > 0 && `(${activeCount})`}
                </button>
                <span className="text-sm text-zinc-400" data-testid="results-count">{products.length} products</span>
              </div>
              <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-[#111] border border-[#27272a] text-sm py-2 px-3 outline-none focus:border-[#00f0ff]">
                {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-square bg-[#0c0c0c] border border-[#1c1c1c] animate-pulse" />)}
              </div>
            ) : products.length === 0 ? (
              activeCount > 0 ? (
                <div className="py-24 text-center text-zinc-400" data-testid="no-results">No products match your filters.</div>
              ) : (
                <div className="py-24 text-center" data-testid="category-coming-soon">
                  <div className="font-display text-4xl md:text-5xl uppercase bttf-text">Coming Soon</div>
                  <p className="text-zinc-400 max-w-md mx-auto mt-4 text-sm">
                    We're onboarding {meta.label.toLowerCase()} from Nitto, Toyo, Falken, Atturo, Mickey Thompson and more. In the meantime, dial in your rims and suspension.
                  </p>
                </div>
              )
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-[90] lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowFilters(false)} />
          <div className="absolute left-0 top-0 h-full w-4/5 max-w-xs bg-[#0a0a0a] border-r border-[#27272a] p-6 overflow-y-auto">
            <button onClick={() => setShowFilters(false)} className="absolute top-4 right-4 text-zinc-400"><X size={22} /></button>
            {Sidebar}
          </div>
        </div>
      )}
    </div>
  );
}
